/**
 * A11 — Onboarding Step 2/4: Invitar al equipo
 *
 * Multi-row invite table with email, nombre, rol.
 * On submit: INSERT invitations into empresa_invite_links or similar.
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Lightbulb } from 'lucide-react';
import { Input } from '@shared/ui/shadcn/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/ui/shadcn/ui/select';
import { Alert, AlertDescription } from '@shared/ui/shadcn/ui/alert';
import { supabase } from '@shared/lib/supabaseClient';
import OnboardingLayout from '../../components/OnboardingLayout';
import { useOnboarding, TeamInvite } from '../../contexts/OnboardingContext';

// ── Roles available ──────────────────────────────────────────────────────────

const ROLES = [
  { value: 'admin', label: 'Asistente Admin' },
  { value: 'vendedor', label: 'Vendedora' },
  { value: 'supervisor', label: 'Supervisor de Taller' },
  { value: 'operario', label: 'Operario' },
  { value: 'tapicero', label: 'Operario - Tapicero' },
  { value: 'ebanista', label: 'Operario - Ebanista' },
  { value: 'costurera', label: 'Operario - Costurera' },
  { value: 'chofer', label: 'Chofer' },
  { value: 'contador', label: 'Contador' },
];

const TeamPage: React.FC = () => {
  const navigate = useNavigate();
  const { state, setTeamInvites } = useOnboarding();
  const { teamInvites, empresaId } = state;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Row helpers ──
  const updateRow = (index: number, field: keyof TeamInvite, value: string) => {
    const updated = [...teamInvites];
    updated[index] = { ...updated[index], [field]: value };
    setTeamInvites(updated);
  };

  const removeRow = (index: number) => {
    if (teamInvites.length <= 1) return;
    setTeamInvites(teamInvites.filter((_, i) => i !== index));
  };

  const addRow = () => {
    setTeamInvites([...teamInvites, { email: '', nombre: '', rol: '' }]);
  };

  // ── Count valid invites ──
  const validInvites = teamInvites.filter(
    (inv) => inv.email.trim() && inv.email.includes('@') && inv.rol
  );

  // ── Submit ──
  const handleNext = async () => {
    if (validInvites.length === 0) {
      // No invites? Just skip to next step
      navigate('/onboarding/negocio');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      // Insert invitations
      if (empresaId) {
        const rows = validInvites.map((inv) => ({
          empresa_id: empresaId,
          email: inv.email.trim().toLowerCase(),
          nombre: inv.nombre.trim() || null,
          rol: inv.rol,
          status: 'pending',
        }));

        const { error: dbErr } = await (supabase as any)
          .from('empresa_invitations')
          .insert(rows);

        if (dbErr) {
          console.error('[Team] Insert error:', dbErr);
          // Non-blocking: table may not exist yet, proceed anyway
        }
      }

      navigate('/onboarding/negocio');
    } catch (err: any) {
      setError(err?.message || 'Error al enviar invitaciones.');
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => navigate('/onboarding/negocio');

  return (
    <OnboardingLayout
      step={2}
      onBack={() => navigate('/onboarding/fiscal')}
      onNext={handleNext}
      nextLabel={validInvites.length > 0 ? `Enviar ${validInvites.length} invitacion${validInvites.length > 1 ? 'es' : ''}` : 'Continuar'}
      loading={loading}
      skipLabel="Invitar despues"
      onSkip={handleSkip}
    >
      <h1 className="text-3xl font-bold tracking-tight">Invita a tu equipo.</h1>
      <p className="text-[15px] text-muted-foreground leading-relaxed max-w-[56ch] mb-6">
        Agrega a las personas que van a usar el workspace. Cada una recibe un link por email para crear su cuenta. Puedes asignar mas de un rol (ej. tu asistente puede ser Admin + Vendedora).
      </p>

      {error && (
        <Alert variant="destructive" className="mb-2">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="my-2">
        {/* Header row */}
        <div className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 pb-1.5 border-b-[1.5px] border-border">
          <div className="font-mono text-[10.5px] tracking-wider uppercase text-muted-foreground font-semibold">
            Email
          </div>
          <div className="font-mono text-[10.5px] tracking-wider uppercase text-muted-foreground font-semibold">
            Nombre (opcional)
          </div>
          <div className="font-mono text-[10.5px] tracking-wider uppercase text-muted-foreground font-semibold">
            Roles
          </div>
          <div className="w-8" />
        </div>

        {/* Invite rows */}
        {teamInvites.map((inv, i) => (
          <div
            key={i}
            className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2 py-2.5 border-b border-border/50"
          >
            <Input
              type="email"
              placeholder="email@ejemplo.com"
              value={inv.email}
              onChange={(e) => updateRow(i, 'email', e.target.value)}
              className="text-[13.5px] py-2 px-2.5"
            />
            <Input
              placeholder="Nombre"
              value={inv.nombre}
              onChange={(e) => updateRow(i, 'nombre', e.target.value)}
              className="text-[13.5px] py-2 px-2.5"
            />
            <Select value={inv.rol} onValueChange={(v) => updateRow(i, 'rol', v)}>
              <SelectTrigger className="text-[13.5px]">
                <SelectValue placeholder="Selecciona rol(es)..." />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r.value} value={r.value}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <button
              type="button"
              onClick={() => removeRow(i)}
              className="w-8 h-8 flex items-center justify-center rounded-md border border-border text-muted-foreground hover:bg-destructive/10 hover:text-destructive hover:border-destructive/50 transition-colors"
              aria-label="Eliminar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}

        {/* Add row button */}
        <button
          type="button"
          onClick={addRow}
          className="text-sm text-primary font-medium pt-3 flex items-center gap-1 hover:underline"
        >
          <Plus className="w-3.5 h-3.5" /> Agregar otra persona
        </button>
      </div>

      {/* Tip box */}
      <div className="bg-primary/5 border border-primary/20 rounded-md px-3.5 py-3 text-[13px] text-muted-foreground mt-2">
        <strong className="text-primary flex items-center gap-1">
          <Lightbulb className="w-3.5 h-3.5" /> Tip:
        </strong>{' '}
        los operarios reciben una app movil distinta -- mas simple, enfocada en &quot;que tienes que hacer hoy&quot;. El chofer recibe ruta con mapa. El admin y vendedora ven la web completa.
      </div>
    </OnboardingLayout>
  );
};

export default TeamPage;
